/**
 * SavedSearchesList — the user's saved searches.
 *
 * Each row shows the query, mode, schedule, and last run; clicking a row links
 * to the detail view. Data comes from {@link useSavedSearches} (TanStack
 * Query) — the component owns no fetch effect.
 */
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import { useMemo, type ReactElement } from "react";
import { useSavedSearches } from "./hooks/useSavedSearches";
import type { SavedSearch } from "@records-monitor/shared";
import { RouterLink } from "@/components/RouterLink";

function queryText(query: SavedSearch["query"]): string {
  const q = query["q"];
  return typeof q === "string" ? q : "(no query)";
}

function formatDate(value: string | null): string {
  if (!value) return "Never";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "Never" : date.toLocaleString();
}

export function SavedSearchesList(): ReactElement {
  const { data, isLoading, isError } = useSavedSearches();
  const rows = useMemo<readonly SavedSearch[]>(() => data?.savedSearches ?? [], [data]);

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress aria-label="Loading saved searches" />
      </Box>
    );
  }

  if (isError) {
    return (
      <Alert severity="error" role="alert">
        Could not load your saved searches.
      </Alert>
    );
  }

  if (rows.length === 0) {
    return (
      <Typography color="text.secondary">
        You have no saved searches yet. Run a search and choose “Save current search”.
      </Typography>
    );
  }

  return (
    <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0 }} aria-label="Saved searches">
      {rows.map((row) => (
        <Box
          component="li"
          key={row.id}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            py: 1.5,
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <RouterLink to="/saved/$id" params={{ id: row.id }} variant="body1">
              {queryText(row.query)}
            </RouterLink>
            <Typography variant="body2" color="text.secondary">
              Last run: {formatDate(row.lastRunAt)}
              {row.schedule ? ` · Schedule: ${row.schedule}` : " · No schedule"}
            </Typography>
          </Box>
          <Chip label={row.type} size="small" />
        </Box>
      ))}
    </Box>
  );
}
